import { Ban, Eye, Loader2, Pencil, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { type AdminUserSummary, useBlockUser, useMe, useUnblockUser } from "@/lib/api";
import { cn } from "@/lib/utils";

import { UserDeleteDialog } from "./user-delete-dialog";
import { UserDetailDialog } from "./user-detail-dialog";
import { UserEditDialog } from "./user-edit-dialog";

interface UserTableProps {
  users: AdminUserSummary[];
}

/**
 * Surface FastAPI's `detail` when there is one.
 *
 * The client's error interceptor wraps every non-OK response as
 * `{ status, body }`, so the sentence worth reading — "you cannot block
 * yourself" on the 400 — lives in `body.detail` rather than on an `Error`.
 */
function errorMessage(error: unknown): string {
  const body = (error as { body?: { detail?: unknown } } | null)?.body;
  if (typeof body?.detail === "string") {
    return body.detail;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Could not change this user's access.";
}

export function UserTable({ users }: UserTableProps) {
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUserSummary | null>(null);
  // Blocking takes someone's access away, so it asks first — inline, in the
  // row, because the row already names who it is about. A dialog would repeat
  // the email back and cost a second click for something that is reversible.
  const [confirmBlockId, setConfirmBlockId] = useState<string | null>(null);
  const [blockError, setBlockError] = useState<{ userId: string; message: string } | null>(null);

  const { data: me } = useMe();
  const blockMutation = useBlockUser();
  const unblockMutation = useUnblockUser();

  const runAccessChange = (userId: string, action: "block" | "unblock") => {
    setBlockError(null);
    const mutation = action === "block" ? blockMutation : unblockMutation;
    mutation.mutate(
      { userId },
      {
        onSuccess: () => setConfirmBlockId(null),
        onError: (error) => setBlockError({ userId, message: errorMessage(error) }),
      },
    );
  };

  if (users.length === 0) {
    return <p className="text-muted py-8 text-center text-sm">No users found.</p>;
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Role</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Joined</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const blocked = user.blocked ?? false;
              // An admin who blocks their own account locks themselves out;
              // the backend refuses it with a 400, and the row does not offer
              // it in the first place. `id` is optional on the profile, so the
              // email — unique, always present — is the fallback check.
              const isSelf = (!!me?.id && me.id === user.id) || me?.email === user.email;
              const isPending =
                (blockMutation.isPending && blockMutation.variables?.userId === user.id) ||
                (unblockMutation.isPending && unblockMutation.variables?.userId === user.id);
              const confirming = confirmBlockId === user.id;
              const error = blockError?.userId === user.id ? blockError.message : null;
              const dimmed = cn("px-4 py-3", blocked && "text-muted opacity-70");

              return (
                <tr
                  key={user.id}
                  className={cn("border-b last:border-0", blocked && "bg-surface-muted/60")}
                >
                  <td className={dimmed}>
                    {user.first_name || user.last_name
                      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                      : "—"}
                  </td>
                  <td className={dimmed}>{user.email}</td>
                  <td className={dimmed}>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                        user.role === "admin"
                          ? "bg-accent/10 text-accent"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {blocked ? (
                      <span className="bg-destructive/10 text-destructive inline-block rounded-full px-2 py-0.5 text-xs font-medium">
                        Blocked
                      </span>
                    ) : (
                      <span className="text-muted text-xs">Active</span>
                    )}
                  </td>
                  <td className={cn(dimmed, "text-muted")}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {confirming ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-muted text-xs">Remove access?</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmBlockId(null)}
                          disabled={isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => runAccessChange(user.id, "block")}
                          disabled={isPending}
                        >
                          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Block"}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setViewUserId(user.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setEditUserId(user.id)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {blocked ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Restore access"
                            aria-label={`Unblock ${user.email}`}
                            onClick={() => runAccessChange(user.id, "unblock")}
                            disabled={isPending}
                          >
                            {isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ShieldCheck className="text-success h-4 w-4" />
                            )}
                          </Button>
                        ) : (
                          // Hidden rather than disabled on your own row: there
                          // is no state in which you can block yourself, so an
                          // inert button would only invite the click.
                          !isSelf && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Remove access"
                              aria-label={`Block ${user.email}`}
                              onClick={() => {
                                setBlockError(null);
                                setConfirmBlockId(user.id);
                              }}
                            >
                              <Ban className="text-destructive h-4 w-4" />
                            </Button>
                          )
                        )}
                        <Button variant="ghost" size="icon" onClick={() => setDeleteUser(user)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                    {error && <p className="text-destructive mt-1 text-xs">{error}</p>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <UserDetailDialog
        userId={viewUserId}
        open={!!viewUserId}
        onOpenChange={(open) => !open && setViewUserId(null)}
      />
      <UserEditDialog
        userId={editUserId}
        open={!!editUserId}
        onOpenChange={(open) => !open && setEditUserId(null)}
      />
      <UserDeleteDialog
        user={deleteUser}
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      />
    </>
  );
}
